/**
 * RedoButton Component
 *
 * Reusable redo button for all simulation pages.
 * Displays redo count and handles click events.
 *
 * @module components/simulation/components/common/RedoButton
 *
 * Props:
 * @param {number} redoCount - Number of available redo operations
 * @param {function} onRedo - Callback when redo is clicked
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} className - Additional CSS classes
 * @param {boolean} showIcon - Whether to show the redo icon (default: true)
 * @param {string} position - Button position: 'left' or 'right' (default: 'right')
 */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../../layout/Button';

const RedoButton = ({
    redoCount,
    onRedo,
    disabled = false,
    className = '',
    showIcon = true,
    position = 'right'
}) => {
    // Don't render if no redo available and disabled
    if (redoCount === 0 && disabled) {
        return null;
    }

    const floatClass = position === 'right' ? 'float-right mr-2' : 'mr-2';
    const buttonClass = `user-btn ${floatClass} ${className}`.trim();

    const title = disabled
        ? 'Redo is not available'
        : `Redo last change (Ctrl+Y) - ${redoCount} change${redoCount !== 1 ? 's' : ''} available`;

    return (
        <Button
            className={buttonClass}
            title={title}
            onClick={onRedo}
            disabled={disabled || redoCount === 0}
        >
            {showIcon && <i className="fa fa-redo mr-1"></i>}
            {`Redo (${redoCount})`}
        </Button>
    );
};

RedoButton.propTypes = {
    redoCount: PropTypes.number.isRequired,
    onRedo: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    showIcon: PropTypes.bool,
    position: PropTypes.oneOf(['left', 'right'])
};

export default RedoButton;


